import type { IdeSelectionContext } from "../selection-context";
import type { StudioAuthorityDomain } from "../studio-authority-workspaces";
import type { RailItemId } from "../types";

export type StudioCockpitTab = "app" | "design" | "guide" | "status";

export type StudioCockpitActivityKind =
  | "working"
  | "changed"
  | "attention"
  | "blocked"
  | "completed";

export interface StudioCockpitDestination {
  domain: StudioAuthorityDomain;
  authorityView: string;
  railItem?: RailItemId;
  projectId?: string;
  targetRef?: string;
  selection?: IdeSelectionContext;
}

export type StudioCockpitResolution =
  | "presented"
  | "explicit-decision"
  | "authoritative-success";

export interface StudioCockpitActivity {
  id: string;
  tab: StudioCockpitTab;
  kind: StudioCockpitActivityKind;
  /**
   * Every member here MUST be emitted by `selectStudioCockpitActivities` —
   * that is the sole construction site (`useStudioCockpit` calls nothing
   * else), so a member no branch of it produces is a name nobody can ever
   * carry. `studio-cockpit-source-reachability.test.ts` compares this
   * declaration against the sources the selector actually emits when driven,
   * and fails on any member it cannot produce. ("publish" was such a member
   * from this file's first commit until 2026-08-19; the only consumer that
   * branched on it — `deriveContextualBlocker` — was therefore dead, and the
   * blocker it would have built was unreachable in production.)
   */
  source: "chat" | "manual" | "webmcp" | "validation";
  title: string;
  targetRef?: string;
  operationId?: string;
  createdAt: string;
  reviewedAt?: string;
  userActionRequired?: boolean;
  destination: StudioCockpitDestination;
  resolution: StudioCockpitResolution;
}

export interface StudioCockpitTabState {
  kind: "idle" | StudioCockpitActivityKind;
  unreadCount: number;
  activities: StudioCockpitActivity[];
}

const TABS: StudioCockpitTab[] = ["app", "design", "guide", "status"];

const PRIORITY = {
  idle: 0,
  completed: 1,
  changed: 2,
  working: 3,
  attention: 4,
  blocked: 5,
} as const;

function activityIdentity(record: StudioCockpitActivity): string {
  return [
    record.operationId ?? record.id,
    record.destination.domain,
    record.destination.authorityView,
    record.targetRef ?? "",
  ].join(":");
}

export function deriveStudioCockpitTabStates(
  records: readonly StudioCockpitActivity[],
): Record<StudioCockpitTab, StudioCockpitTabState> {
  return Object.fromEntries(
    TABS.map((tab) => {
      const unique = new Map<string, StudioCockpitActivity>();

      for (const record of records) {
        if (record.tab !== tab) continue;
        const key = activityIdentity(record);
        const previous = unique.get(key);
        if (!previous || previous.createdAt < record.createdAt) {
          unique.set(key, record);
        }
      }

      const activities = [...unique.values()];
      const unreadCount = activities.filter(
        (record) =>
          record.reviewedAt === undefined &&
          record.userActionRequired !== false,
      ).length;
      const kind = activities.reduce<StudioCockpitTabState["kind"]>(
        (current, record) =>
          PRIORITY[record.kind] > PRIORITY[current] ? record.kind : current,
        "idle",
      );

      return [tab, { kind, unreadCount, activities }];
    }),
  ) as Record<StudioCockpitTab, StudioCockpitTabState>;
}

export function selectCockpitAttentionRoute(
  state: StudioCockpitTabState,
): StudioCockpitActivity | null {
  return (
    state.activities
      .filter(
        (activity) =>
          activity.reviewedAt === undefined &&
          activity.userActionRequired !== false,
      )
      .sort(
        (left, right) =>
          PRIORITY[right.kind] - PRIORITY[left.kind] ||
          right.createdAt.localeCompare(left.createdAt),
      )[0] ?? null
  );
}
